<template>
    <div
        :class="[
            {
                highlighted: isSelected,
                'border-t-2 border-b-2 md:border-r-2 md:border-l-2 border-blue-300': post.isPinned,
                isStickyEditPost: isStickyEditPost,
            },
            'bg-white shadow md:rounded-lg pt-3 md:pt-5 max-md:container md:px-6 post-item protected-auth protected-verified flex-col flex items-stretch',
        ]"
        :style="{ top: `${topBarHeight + 16}px` }"
    >
        <PostNsfw
            v-if="post.postDetails.is_nsfw"
            :post="post"
            :show-post-header="!isPostDetailsPage"
        />

        <div v-else>
            <PostEdit
                v-if="edit"
                :post-object="post"
                @close-edit="
                    edit = false;
                    isStickyEditPost = false;
                "
            ></PostEdit>

            <template v-else>
                <PostHeader
                    :post="post"
                    class="mb-3"
                    @delete-post="confirmDeletePost"
                    @edit-post="editPost"
                    @pin-unpin-post="togglePinnedPost"
                    @report-post="reportPost"
                />

                <div>
                    <div class="leading-6 mb-4 md:mb-3 exclude-auth exclude-verified w-full">
                        <CollapsibleText
                            :content="post.postDetails.content"
                            show-more-appearance="font-roboto text-slate-400"
                            class="highlight-links"
                        />
                        <PostImages
                            v-if="postImages.length"
                            :files="postImages"
                            class="mt-3"
                        ></PostImages>
                    </div>
                    <PostPoll
                        v-if="hasPoll"
                        :context="context"
                        :poll="post.postDetails.poll"
                        class="mb-3"
                    ></PostPoll>
                </div>

                <PostFooter
                    :post="post"
                    :hide-help-button="hideHelpButton"
                    class="mb-3"
                    @focus-comment-input="focusCommentInput"
                    @edit-post="editPost"
                    @delete-post="confirmDeletePost"
                    @pin-unpin-post="togglePinnedPost"
                    @report-post="reportPost"
                    @scroll-to-mark="(marking) => $emit('scroll-to-mark', marking)"
                    @scroll-best-answer="scrollToBestAnswer"
                ></PostFooter>
            </template>
            <div
                v-if="commentsAreLoading"
                class="border-t flex px-8 py-2 w-full h-11"
            >
                <Spinner
                    class="float-left"
                    color="blue"
                    size="xs"
                />
            </div>

            <Btn
                v-else-if="hasMoreCommentsToShow"
                :variant="BtnVariant.TEXT"
                class="border-t"
                color="slate-400"
                :size="BtnSize.SM"
                :label="
                    commentsAreHidden
                        ? $t('post:seeAllComments', { total: post.totalComments })
                        : $t('post:hideComments')
                "
                @click="toggleComments"
            />

            <PostCommentList
                ref="commentList"
                :post="post"
                :comments="displayedComments"
            />

            <PostCommentCreate
                ref="commentInput"
                :post="post"
                :context="context"
                :context-id="contextId"
                :is-anonymous="isAnonymous"
            ></PostCommentCreate>
        </div>
    </div>
</template>

<script>
import { defineAsyncComponent, inject } from 'vue';
import { provideDeleteMarkingKey } from '@/components/keys';
import { BtnVariant, BtnSize } from '@components/Btn';
import CollapsibleText from '@components/CollapsibleText';
import PostEdit from '@components/PostEdit';
import { mapGetters } from 'vuex';
import PostPoll from './PostPoll.vue';
import PostHeader from './PostHeader.vue';
import PostFooter from './PostFooter.vue';
import PostCommentList from './PostCommentList.vue';
import PostCommentCreate from './PostCommentCreate.vue';
import PostNsfw from './PostNsfw.vue';
import PostImages from './PostImages.vue';

const numberOfAlwaysDisplayedComments = 2;

export default {
    name: 'PostItem',
    components: {
        PostNsfw,
        PostCommentList,
        PostEdit,
        CollapsibleText,
        PostCommentCreate,
        PostFooter,
        PostHeader,
        PostPoll,
        PostImages,
    },
    props: {
        post: {
            type: Object,
            required: true,
        },
        hideHelpButton: {
            type: Boolean,
            default: false,
        },
        selectedPost: {
            type: Number,
            default: null,
        },
        isPostDetailsPage: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['scroll-to-mark'],
    setup() {
        const deleteMarking = inject(provideDeleteMarkingKey);
        return { deleteMarking };
    },
    data() {
        return {
            BtnVariant,
            BtnSize,
            isStickyEditPost: false,
            commentsAreHidden: true,
            commentsAreLoading: false,
            edit: false,
            hasFetchedAllComments: false,
        };
    },
    computed: {
        ...mapGetters('ui', ['module', 'topBarHeight']),
        comments() {
            return this.post.comments;
        },
        contextId() {
            const { context } = this.post.postDetails;
            return this.post[context].id;
        },
        context() {
            return this.post.postDetails.context;
        },
        displayedComments() {
            if (!this.commentsAreHidden) {
                return this.comments;
            }
            return this.comments.slice(-1 * numberOfAlwaysDisplayedComments);
        },
        hasMoreCommentsToShow() {
            if (this.module === 'postDetail') {
                return false;
            }
            return this.post.totalComments > numberOfAlwaysDisplayedComments;
        },
        hasPoll() {
            return this.post.postDetails.hasPoll;
        },
        loadPostCommentsAction() {
            return `${this.module}/loadPostComments`;
        },
        postImages() {
            return this.post.postDetails.files || [];
        },
        isAnonymous() {
            const { isOwner, postDetails, comments } = this.post;
            if (isOwner && postDetails.isAnonymous) {
                return true;
            }
            const anonymous = comments.filter((comment) => {
                return comment.isOwner && comment.isAnonymous;
            });
            return anonymous.length > 0;
        },
        isSelected() {
            return this.selectedPost === this.post.postDetails.id;
        },
        modalTranslations() {
            return {
                title: this.$t('post:popupDelete:headline'),
                submitText: this.$t('post:popupDelete:submit'),
            };
        },
    },
    mounted() {
        if (this.module === 'postDetail') {
            this.commentsAreHidden = false;
        }
    },
    methods: {
        focusCommentInput() {
            this.$refs.commentInput.focusCommentInput();
        },
        async confirmDeletePost() {
            const confirmed = await this.$modal.confirmDanger({
                title: this.modalTranslations.title,
                submitText: this.modalTranslations.submitText,
            });

            if (!confirmed) return;

            await this.$store.dispatch(`${this.module}/deletePost`, {
                context: this.context,
                postId: this.post.postDetails.id,
            });

            this.deleteMarking?.(this.post.postDetails.id);

            this.$store.dispatch('flashMessages/showSuccess', {
                content: this.$t('post:popupDelete:success'),
                progress: { time: 3000, showProgress: false },
            });
        },
        async togglePinnedPost() {
            const data = {
                postId: this.post.postDetails.id,
                context: this.context,
                targetId: this.post[this.context].id,
            };
            this.$store
                .dispatch(`${this.module}/togglePinnedPost`, data)
                .then(() => {
                    window.location.reload();
                })
                .catch(() => {
                    this.$store.dispatch('flashMessages/showError', {
                        content: this.$t('post:flashMessages:error:pinUnpin'),
                    });
                });
        },
        editPost() {
            this.isStickyEditPost = true;
            this.edit = true;
        },
        async toggleComments() {
            if (this.hasFetchedAllComments) {
                this.commentsAreHidden = !this.commentsAreHidden;
                return false;
            }

            this.commentsAreLoading = true;

            try {
                await this.$store.dispatch(this.loadPostCommentsAction, {
                    postId: this.post.postDetails.id,
                    context: this.context,
                });

                this.hasFetchedAllComments = true;
                this.commentsAreHidden = false;
                this.commentsAreLoading = false;
                return true;
            } catch (error) {
                this.commentsAreHidden = true;
                this.commentsAreLoading = false;
                throw error; // throw error again to prevent infinite toggelComments invocation in getBestAnswer()
            }
        },
        reportPost() {
            this.$modal.open(
                defineAsyncComponent(() => import('@/components/ModalReportPost')),
                {
                    postId: this.post.postDetails.id,
                    context: this.context,
                    module: this.module,
                },
            );
        },
        async getBestAnswer() {
            const bestAnswer = this.comments ? this.comments.find((comment) => comment.isBest) : undefined;

            if (bestAnswer) return bestAnswer;

            if (this.commentsAreHidden) await this.toggleComments();

            await this.$nextTick();
            return this.getBestAnswer();
        },
        async scrollToBestAnswer() {
            const bestAnswer = await this.getBestAnswer();
            this.commentsAreHidden = false;

            return this.$refs.commentList.scrollToComment(bestAnswer.id);
        },
    },
};
</script>

<style lang="scss" scoped>
.highlighted {
    box-shadow:
        inset 0 3px theme('colors.blue.400'),
        inset 0 -3px theme('colors.blue.400');
}

@screen md {
    .highlighted {
        box-shadow: inset 0 0 0 3px theme('colors.blue.400');
    }
    .isStickyEditPost {
        position: sticky;
        z-index: 41;
        bottom: 1rem;
    }
}
</style>
